// src/pages/Legal/FAQPage.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../../components/MainLayout/MainLayout';
import useSEO from '../../hooks/useSEO';
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  Search,
  Play,
  Users,
  CreditCard,
  Shield,
  Upload,
  Settings,
  MessageCircle,
  Star,
  Info
} from 'lucide-react';

/**
 * PLATFORM STATUS: Single Creator Model (Current)
 * 
 * FAQ content reflects the current single-creator model where all content is
 * uploaded by the platform administrator.
 * 
 * FUTURE PLANS (Estimated: Late 2025/2026):
 * Creator and monetization FAQs are commented out and will be activated when
 * the platform opens to external content creators.
 * 
 * Version: 1.0.0 - Single Creator Model
 * Next Version: 2.0.0 - Creator Open Model (planned)
 */

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const FAQPage = () => {
  // Apply SEO for FAQ page with FAQ structured data
  useSEO({
    title: 'Frequently Asked Questions - StrymHub Help Center',
    description: 'Find answers to common questions about StrymHub. Learn how to create an account, upload content, monetize your videos, and more. Get help with our video streaming platform.',
    keywords: 'strymhub faq, help center, frequently asked questions, how to use strymhub, creator questions, viewer questions, streaming help, upload videos, monetization',
    type: 'website'
  });

  const [openItems, setOpenItems] = useState<Set<string>>(new Set(['getting-started-1']));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: Play },
    { id: 'platform-status', name: 'Platform Status', icon: Star },
    { id: 'account', name: 'Account & Profile', icon: Users },
    // INVITE-ONLY: Creator categories - May be activated for select partnerships
    // { id: 'creators', name: 'For Creators', icon: Upload },
    // { id: 'monetization', name: 'Monetization', icon: CreditCard },
    { id: 'future-creators', name: 'Future Creator Program', icon: Upload },
    { id: 'technical', name: 'Technical Support', icon: Settings },
    { id: 'privacy', name: 'Privacy & Safety', icon: Shield },
  ];

  const faqs: FAQItem[] = [
    // Getting Started
    {
      id: 'getting-started-1',
      question: 'What is StrymHub and how does it work?',
      answer: 'StrymHub is a company-owned video streaming platform. All content is 100% original — conceived, filmed, and edited by the founder. Nothing is copied from other platforms. We host original films, podcasts, animations, and exclusive content. Viewers can browse our library, create playlists, and discover quality content. The platform may also feature content promoting StrymHub\'s affiliated businesses and platforms.',
      category: 'getting-started'
    },
    {
      id: 'getting-started-2',
      question: 'How do I create an account on StrymHub?',
      answer: 'Creating a StrymHub account is free and simple. Click the "Sign Up" button on our homepage, enter your email address, create a password, and verify your email. You can also sign up using your Google account for faster registration. Once registered, you can immediately start watching content, building playlists, and engaging with the community.',
      category: 'getting-started'
    },
    {
      id: 'getting-started-3',
      question: 'Is StrymHub free to use?',
      answer: 'Yes! StrymHub is completely free to use. We don\'t have any payment or subscription features yet — all content is freely available to registered viewers. In the future, we may introduce premium content options, but a significant portion of content will always remain free for our community.',
      category: 'getting-started'
    },
    {
      id: 'getting-started-4',
      question: 'What types of content can I find on StrymHub?',
      answer: 'StrymHub features diverse content across multiple categories: Original Cinema (short films, documentaries, feature films), Podcasts (video and audio shows covering various topics), Animations (2D, 3D, motion graphics, and animated series), Behind-the-Scenes content (making-of features, sneak peeks), and Live Streams (real-time broadcasts and events). All content is currently curated and uploaded by our platform administrator to ensure quality.',
      category: 'getting-started'
    },

    // Platform Status (New Section)
    {
      id: 'platform-status-1',
      question: 'Why is StrymHub a company platform?',
      answer: 'StrymHub is designed as a company-owned content platform, not a public creator platform. All content is created by StrymHub and its founder to ensure consistent quality standards and a cohesive viewing experience. This model allows us to maintain full control over content quality and use the platform to promote our affiliated businesses and platforms.',
      category: 'platform-status'
    },
    {
      id: 'platform-status-2',
      question: 'Can I become a content creator on StrymHub?',
      answer: 'StrymHub is not a public creator platform. However, we may consider invite-only partnerships with select creators in the future. These partnerships would be at StrymHub\'s sole discretion and are not guaranteed. If you believe you\'d be a great fit for a partnership, you can reach out to us at partnerships@strymhub.com.',
      category: 'platform-status'
    },
    {
      id: 'platform-status-3',
      question: 'How can I stay updated on platform changes?',
      answer: 'The best way to stay updated is to create an account and opt-in to our newsletter. We\'ll notify all users via email when major platform changes occur, including the transition to our Creator Open Model. You can also follow our social media channels or check our About page for the latest announcements.',
      category: 'platform-status'
    },
    {
      id: 'platform-status-4',
      question: 'Can I request specific content on StrymHub?',
      answer: 'Absolutely! We welcome content suggestions from our community. Since we currently manage all content creation, your input helps us decide what to create next. Contact us through our contact form or email with your suggestions, and we\'ll consider them for future content.',
      category: 'platform-status'
    },

    // Account & Profile
    {
      id: 'account-1',
      question: 'How do I edit my profile information?',
      answer: 'To edit your profile, log into your StrymHub account and click on your profile picture in the top right corner. Select "Settings" or "Edit Profile" from the dropdown menu. From there, you can update your display name, profile picture, bio, and notification preferences. Remember to save your changes before leaving the page.',
      category: 'account'
    },
    {
      id: 'account-2',
      question: 'How do I reset my password?',
      answer: 'If you\'ve forgotten your password, click the "Sign In" button, then select "Forgot Password?" Enter the email address associated with your account, and we\'ll send you a password reset link. The link expires after 24 hours for security. If you don\'t receive the email, check your spam folder or contact our support team.',
      category: 'account'
    },
    {
      id: 'account-3',
      question: 'Can I delete my StrymHub account?',
      answer: 'Yes, you can delete your account at any time. Go to Settings > Account > Delete Account. Please note that account deletion is permanent and will remove all your playlists, favorites, and viewing history. We retain some data for legal compliance as outlined in our Privacy Policy.',
      category: 'account'
    },

    // Future Creator Program (New Section replacing Creators)
    {
      id: 'future-creators-1',
      question: 'How can I inquire about a creator partnership?',
      answer: 'While StrymHub is a company platform, we may consider invite-only partnerships with select creators. If you\'re interested, you can email partnerships@strymhub.com with information about your content style, experience, and links to your existing work. Please note that we do not accept public applications — partnerships are offered by invitation at our discretion.',
      category: 'future-creators'
    },
    {
      id: 'future-creators-2',
      question: 'What would a creator partnership involve?',
      answer: 'If invited for a partnership, creators would collaborate with StrymHub on content that aligns with our platform\'s standards and vision. Partnership details would be discussed individually, but may include content licensing, revenue sharing arrangements, and promotional support. All partner content would need to meet StrymHub\'s quality and content guidelines.',
      category: 'future-creators'
    },
    {
      id: 'future-creators-3',
      question: 'What types of content does StrymHub feature?',
      answer: 'StrymHub features 100% original content including films, documentaries, podcasts, animations, and exclusive series. All content is created by the founder or through approved partnerships. We may also feature content that promotes StrymHub\'s affiliated businesses and platforms as part of our company ecosystem.',
      category: 'future-creators'
    },
    {
      id: 'future-creators-4',
      question: 'Are there any requirements for creator partnerships?',
      answer: 'Creator partnerships are invitation-only and at StrymHub\'s sole discretion. If we consider a partnership, we look for creators who produce high-quality original content, align with our platform\'s values and aesthetic, have a track record of consistent content creation, and can meet our content guidelines and quality standards. We do not accept public applications.',
      category: 'future-creators'
    },

    /* 
     * INVITE-ONLY: Creator Terms - May be activated for select partnerships
     * 
    // For Creators
    {
      id: 'creators-1',
      question: 'How do I become a creator on StrymHub?',
      answer: 'Any registered user can become a creator on StrymHub. After signing up, go to your dashboard and click "Become a Creator" or "Creator Studio." Complete your creator profile with a bio, profile picture, and links to your other social platforms. Accept our Creator Terms of Service, and you\'re ready to upload content. We don\'t require a minimum subscriber count to start uploading.',
      category: 'creators'
    },
    {
      id: 'creators-2',
      question: 'What file formats and sizes does StrymHub accept?',
      answer: 'StrymHub accepts most common video formats including MP4, MOV, AVI, MKV, and WebM. We recommend MP4 with H.264 encoding for best compatibility. Maximum file size is 10GB for standard creators and 50GB for verified creators. We support resolutions up to 4K (3840x2160). For podcasts, we accept MP3, WAV, and AAC audio files up to 2GB.',
      category: 'creators'
    },
    {
      id: 'creators-3',
      question: 'How long does it take for my video to process?',
      answer: 'Processing time depends on your video\'s length, resolution, and our current server load. Typically, a 10-minute 1080p video processes in 5-15 minutes. 4K videos and longer content may take 30 minutes to an hour. During processing, we create multiple quality versions for adaptive streaming. You\'ll receive a notification when your video is ready and published.',
      category: 'creators'
    },
    {
      id: 'creators-4',
      question: 'Can I schedule content to be published later?',
      answer: 'Yes! When uploading content, you can choose to publish immediately or schedule it for a future date and time. This is useful for coordinating releases across platforms, targeting peak viewing times, or building anticipation with your audience. You can also save content as drafts and return to finish them later.',
      category: 'creators'
    },
    {
      id: 'creators-5',
      question: 'How do I get my content featured on StrymHub?',
      answer: 'Our editorial team regularly curates content for featured sections. To increase your chances: maintain high production quality, engage with your community, publish consistently, use accurate titles and descriptions, add appropriate tags, create eye-catching thumbnails, and follow our community guidelines. Exceptional content that resonates with audiences has the best chance of being featured.',
      category: 'creators'
    },

    // Monetization
    {
      id: 'monetization-1',
      question: 'How do I earn money on StrymHub?',
      answer: 'Creators can earn money through multiple channels: Ad Revenue (share of advertising revenue from your content), Tips & Donations (viewers can send direct tips), Subscriptions (offer exclusive content to paying subscribers), and Pay-Per-View (charge for premium content). To enable monetization, you need to be in good standing with our community guidelines, have at least 100 subscribers, and link a valid payment method.',
      category: 'monetization'
    },
    {
      id: 'monetization-2',
      question: 'What percentage of revenue do creators receive?',
      answer: 'StrymHub offers competitive revenue sharing: creators receive 70% of ad revenue generated by their content, 85% of tips and donations, 80% of subscription revenue, and 80% of pay-per-view purchases. These rates are among the most competitive in the industry, reflecting our commitment to supporting independent creators.',
      category: 'monetization'
    },
    {
      id: 'monetization-3',
      question: 'When and how do I get paid?',
      answer: 'Payments are processed monthly, around the 15th of each month, for earnings accumulated in the previous month. The minimum payout threshold is $50 USD. We support payments via PayPal, bank transfer (in select countries), and Stripe. You can track your earnings in real-time through your Creator Dashboard.',
      category: 'monetization'
    },
    {
      id: 'monetization-4',
      question: 'Do I need to pay taxes on my StrymHub earnings?',
      answer: 'Yes, you are responsible for reporting and paying taxes on your StrymHub earnings according to your local tax laws. StrymHub provides year-end earnings statements for tax purposes. For US creators earning over $600 annually, we\'ll send a 1099 form. We recommend consulting a tax professional for guidance specific to your situation.',
      category: 'monetization'
    },
    */

    // Technical Support
    {
      id: 'technical-1',
      question: 'Why is my video buffering or not loading?',
      answer: 'Video playback issues are usually caused by: slow internet connection (try lowering the video quality), browser cache issues (clear your cache and cookies), outdated browser (update to the latest version), or ad blockers interfering with playback. If problems persist, try a different browser or device. For consistent issues, contact our support team with details about your device and browser.',
      category: 'technical'
    },
    {
      id: 'technical-2',
      question: 'What devices and browsers does StrymHub support?',
      answer: 'StrymHub works on most modern devices and browsers. We support Chrome, Firefox, Safari, and Edge (latest versions). On mobile, we support iOS 14+ and Android 8+. We also have apps for iOS and Android available in the respective app stores. Smart TV apps are coming soon. For the best experience, we recommend using the latest version of Chrome or our mobile apps.',
      category: 'technical'
    },
    {
      id: 'technical-3',
      question: 'How do I download videos for offline viewing?',
      answer: 'Offline viewing is available for select content through our mobile apps. Look for the download icon on supported videos. Downloaded content remains available for 30 days. Not all content is available for download—this depends on content settings and licensing agreements.',
      category: 'technical'
    },

    // Privacy & Safety
    {
      id: 'privacy-1',
      question: 'How does StrymHub protect my personal data?',
      answer: 'We take data protection seriously. All data is encrypted in transit and at rest using industry-standard encryption. We never sell your personal data to third parties. We collect only the data necessary to provide our services. You can download or delete your data at any time through your account settings. For full details, please read our Privacy Policy.',
      category: 'privacy'
    },
    {
      id: 'privacy-2',
      question: 'How do I report inappropriate content or behavior?',
      answer: 'If you encounter content or behavior that violates our Community Guidelines, click the three-dot menu on the video or comment and select "Report." Choose the appropriate reason for your report and provide any additional details. Our moderation team reviews all reports within 24-48 hours. For urgent safety concerns, contact us directly at safety@strymhub.com.',
      category: 'privacy'
    },
    {
      id: 'privacy-3',
      question: 'Can I make my viewing history private?',
      answer: 'Yes, you have full control over your privacy. Go to Settings > Privacy to manage your preferences. You can pause viewing history, clear past history, make your profile private, hide your liked videos, and control who can see your favorites. Private profiles are only visible to you and won\'t appear in search results.',
      category: 'privacy'
    },
  ];

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !activeCategory || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedFaqs = categories.map(category => ({
    ...category,
    faqs: filteredFaqs.filter(faq => faq.category === category.id)
  })).filter(cat => cat.faqs.length > 0);

  // Generate FAQ structured data for SEO
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <MainLayout>
      {/* FAQ Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center">
                  <HelpCircle className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Frequently Asked <span className="text-transparent font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text">Questions</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
                Find answers to common questions about StrymHub. Can't find what you're looking for? 
                Our support team is always here to help.
              </p>

              {/* Search Bar */}
              <div className="max-w-xl mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  !activeCategory 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Topics
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`inline-flex items-center px-4 py-2 rounded-full font-medium transition-colors ${
                    activeCategory === category.id 
                      ? 'bg-orange-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <category.icon className="h-4 w-4 mr-2" />
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-6">
                  We couldn't find any questions matching your search. Try different keywords or browse all topics.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 transition-colors"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Contact Support
                </Link>
              </div>
            ) : (
              <div className="space-y-12">
                {groupedFaqs.map(category => (
                  <div key={category.id}>
                    <div className="flex items-center mb-6">
                      <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-4">
                        <category.icon className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">{category.name}</h2>
                    </div>
                    
                    <div className="space-y-4">
                      {category.faqs.map(faq => (
                        <div
                          key={faq.id}
                          className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                        >
                          <button
                            onClick={() => toggleItem(faq.id)}
                            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                          >
                            <span className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</span>
                            {openItems.has(faq.id) ? (
                              <ChevronUp className="h-5 w-5 text-orange-600 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                          
                          {openItems.has(faq.id) && (
                            <div className="px-6 pb-6">
                              <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Still Need Help Section */}
        <section className="py-16 bg-gradient-to-r from-orange-600 to-red-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our friendly support team is always ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Contact Support
              </Link>
              <a
                href="mailto:info@strymhub.com"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
              >
                Email Us Directly
              </a>
            </div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default FAQPage;
